import React, { useMemo } from 'react';
import {
  defineMessages,
  FormattedMessage,
  useIntl,
  type MessageValue,
  type NoMessageValues,
} from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { AnimatedNumberText, ButtonFluid, GlassView, theme, useResponsive } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationCTA = (props: CustomizationCTAProps) => {
  const {
    variation,
    isLiquidGlassEnabled = false,
    cost,
    calories,
    quantity,
    disabled,
    onPress,
  } = props;

  const { formatNumber, formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  // ─── Derived Data ────────────────────────────────────────────────────

  const shouldRenderGlass = isLiquidGlassEnabled && !disabled;

  const formattedCost = useMemo(() => {
    const formattedValue = formatNumber(cost / 100, { style: 'currency', currency: 'USD' });

    if (quantity > 1) {
      return formatMessage(messages.costPerProduct, { cost: formattedValue });
    }

    return formattedValue;
  }, [formatNumber, cost, quantity, formatMessage]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const pressableVariantStyle = useMemo(() => {
    if (currentBreakpoint.isXS) {
      return styles.pressableNewXS;
    }

    return styles.pressableNewSM;
  }, [currentBreakpoint.isXS]);

  const buttonTextStyle = useMemo(() => {
    if (!disabled) {
      return styles.buttonTextNew;
    }
  }, [disabled]);

  const costTextStyle = useMemo(() => {
    if (disabled) {
      return styles.costTextDisabled;
    }

    return styles.buttonTextNew;
  }, [disabled]);

  const pressableStyles = [
    styles.pressableBase,
    pressableVariantStyle,
    shouldRenderGlass ? styles.pressableGlass : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ButtonFluid.Pressable style={pressableStyles} disabled={disabled} onPress={onPress}>
      {shouldRenderGlass ? <GlassView glassEffectStyle="clear" style={styles.glass} /> : null}

      <ButtonFluid.Label disabled={disabled} sizeMatch={['18']} style={buttonTextStyle}>
        {variation === 'continue' ? <FormattedMessage {...messages.continueLabel} /> : null}
        {variation === 'add-to-bag' ? <FormattedMessage {...messages.addToBagLabel} /> : null}
        {variation === 'update' ? <FormattedMessage {...messages.updateLabel} /> : null}
      </ButtonFluid.Label>

      <View style={styles.textContainer}>
        {typeof calories === 'number' ? (
          <ButtonFluid.Text disabled={disabled} sizeMatch={['16']} style={buttonTextStyle}>
            <FormattedMessage {...messages.calories} values={{ calories }} />
          </ButtonFluid.Text>
        ) : null}

        <AnimatedNumberText.BodyText
          text={formattedCost}
          animateInitialValue={false}
          duration={PRICE_ANIMATION_DURATION}
          sizeMatch={['16']}
          style={costTextStyle}
        />
      </View>
    </ButtonFluid.Pressable>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PRICE_ANIMATION_DURATION = 1_000;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly continueLabel: NoMessageValues;
  readonly addToBagLabel: NoMessageValues;
  readonly updateLabel: NoMessageValues;
  readonly calories: { readonly calories: MessageValue };
  readonly costPerProduct: { readonly cost: MessageValue };
}>({
  continueLabel: {
    defaultMessage: 'Continue',
    description: 'Customization | CTA | "Continue" label',
  },
  addToBagLabel: {
    defaultMessage: 'Add to bag',
    description: 'Customization | CTA | "Add to bag" label',
  },
  updateLabel: {
    defaultMessage: 'Update',
    description: 'Customization | CTA | "Update" label',
  },
  calories: {
    defaultMessage: '{calories} cal',
    description: 'Customization | CTA | Calories',
  },
  costPerProduct: {
    defaultMessage: '{cost}/ea',
    description: 'Customization | CTA | Cost per product',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pressableBase: {
    borderRadius: theme.radius['3xLarge'],
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 0, // override default style
    paddingTop: 0, // override default style
    paddingBottom: 0, // override default style
  },
  pressableNewXS: {
    boxShadow: '0px 0px 20px 0px rgba(14, 21, 14, 0.05)',
    width: 'auto', // override default style
    minWidth: 215,
    minHeight: 56,
    maxHeight: 56,
    gap: theme.spacing['6'],
    paddingHorizontal: theme.spacing['6'],
  },
  pressableNewSM: {
    boxShadow: '0px 0px 20px 0px rgba(14, 21, 14, 0.05)',
    minWidth: 287,
    minHeight: 72,
    maxHeight: 72,
    gap: theme.spacing['6'],
    paddingHorizontal: theme.spacing['8'],
  },
  pressableGlass: {
    backgroundColor: 'rgba(0, 71, 60, 0.95)',
  },
  glass: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius['3xLarge'],
    pointerEvents: 'none',
  },
  buttonTextNew: {
    color: theme.colors.OATMEAL,
  },
  textContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
    columnGap: theme.spacing['3'],
  },
  costTextDisabled: {
    color: theme.colors.OPACITY.DARK_KALE.DARK,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationCTAProps = {
  variation: 'add-to-bag' | 'continue' | 'update';
  isLiquidGlassEnabled?: boolean;
  cost: number;
  calories?: number;
  quantity: number;
  disabled?: boolean;
  onPress: () => void;
};
